import { useMemo } from 'react';
import { useLanguagePairContext } from 'src/context/languagePair';
import { getExternalHref } from 'src/utils/getExternalHref';
import type * as GetExternalHrefTypes from 'src/utils/getExternalHref';

type PageParams<T> = {
  linkType: T;
  query?: GetExternalHrefTypes.Query;
};

export type MemriseAboutUsParams = PageParams<
  GetExternalHrefTypes.MemriseAboutUsParams['linkType']
>;

export type MemriseBlogParams = PageParams<GetExternalHrefTypes.MemriseBlogParams['linkType']>;

export type MemriseContactUsParams = PageParams<
  GetExternalHrefTypes.MemriseContactUsParams['linkType']
>;

export type MemriseCookiesPolicyParams = PageParams<
  GetExternalHrefTypes.MemriseCookiesPolicyParams['linkType']
>;

export type MemriseEngineeringBlogParams = PageParams<
  GetExternalHrefTypes.MemriseEngineeringBlogParams['linkType']
>;

export type MemriseHelpParams = PageParams<GetExternalHrefTypes.MemriseHelpParams['linkType']>;

export type MemriseJobsParams = PageParams<GetExternalHrefTypes.MemriseJobsParams['linkType']>;

export type MemriseAppStoreParams = PageParams<
  GetExternalHrefTypes.MemriseAppStoreParams['linkType']
>;

export type MemriseGooglePlayParams = PageParams<
  GetExternalHrefTypes.MemriseGooglePlayParams['linkType']
>;

export type MemriseLearnLanguageCustomParams = PageParams<
  GetExternalHrefTypes.MemriseLearnLanguageCustomParams['linkType']
>;

export type MemriseLearnLanguageParams = PageParams<
  GetExternalHrefTypes.MemriseLearnLanguageParams['linkType']
>;

export type MemriseLocalizedMainPageParams = PageParams<
  GetExternalHrefTypes.MemriseLocalizedMainPageParams['linkType']
>;

export type MemriseMainPageParams = PageParams<
  GetExternalHrefTypes.MemriseMainPageParams['linkType']
>;

export type MemriseMobileAppParams = PageParams<
  GetExternalHrefTypes.MemriseMobileAppParams['linkType']
>;

export type MemrisePhilosophyParams = PageParams<
  GetExternalHrefTypes.MemrisePhilosophyParams['linkType']
>;

export type MemrisePhrasebooksParams = PageParams<
  GetExternalHrefTypes.MemrisePhrasebooksParams['linkType']
>;

export type MemrisePrivacyPolicyParams = PageParams<
  GetExternalHrefTypes.MemrisePrivacyPolicyParams['linkType']
>;

export type MemriseTeamParams = PageParams<GetExternalHrefTypes.MemriseTeamParams['linkType']>;

export type MemriseTermsOfUseParams = PageParams<
  GetExternalHrefTypes.MemriseTermsOfUseParams['linkType']
>;

export type MemriseLocalizedSignInParams = PageParams<
  GetExternalHrefTypes.MemriseLocalizedSignInParams['linkType']
>;

export type MemriseSignUpParams = PageParams<GetExternalHrefTypes.MemriseSignUpParams['linkType']>;

export type MemriseMembotParams = PageParams<
  GetExternalHrefTypes.MemriseMembotParams['linkType']
> & {
  query: GetExternalHrefTypes.Query & {
    slug: string;
    back: string;
  };
};

export type MemrisePaymentPlansParams = PageParams<
  GetExternalHrefTypes.MemrisePaymentPlansParams['linkType']
>;

export type RequiredExternalHrefParams =
  | MemriseCookiesPolicyParams
  | MemriseContactUsParams
  | MemriseHelpParams
  | MemriseAppStoreParams
  | MemriseGooglePlayParams
  | MemriseLearnLanguageParams
  | MemriseLocalizedMainPageParams
  | MemriseMainPageParams
  | MemriseMobileAppParams
  | MemrisePrivacyPolicyParams
  | MemriseTermsOfUseParams
  | MemriseLocalizedSignInParams
  | MemriseSignUpParams
  | MemriseMembotParams
  | MemrisePaymentPlansParams;

export type OptionalExternalHrefParams =
  | MemriseAboutUsParams
  | MemriseBlogParams
  | MemriseEngineeringBlogParams
  | MemriseJobsParams
  | MemriseLearnLanguageCustomParams
  | MemrisePhilosophyParams
  | MemrisePhrasebooksParams
  | MemriseTeamParams;

export type ExternalHrefParams = RequiredExternalHrefParams | OptionalExternalHrefParams;

export function useExternalHref(q: RequiredExternalHrefParams): string;
export function useExternalHref(q: ExternalHrefParams): string | null;
export function useExternalHref(q: ExternalHrefParams): string | null {
  const { languagePairId, sourceLanguageLocale, targetLanguageLocale } = useLanguagePairContext();

  return useMemo(() => {
    switch (q.linkType) {
      case 'memrise-about-us': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-blog': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-contact-us': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-cookies-policy': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-engineering-blog': {
        return getExternalHref(q);
      }

      case 'memrise-help': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-jobs': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-app-store': {
        return getExternalHref(q);
      }

      case 'memrise-google-play': {
        return getExternalHref(q);
      }

      case 'memrise-learn-language-custom': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-learn-language': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            targetLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-localized-main-page': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-main-page': {
        return getExternalHref(q);
      }

      case 'memrise-mobile-app': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-philosophy': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-phrasebooks': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-privacy-policy': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-team': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-terms-of-use': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-localized-signin': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-signup': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            sourceLanguageLocale,
            targetLanguageLocale,
            ...q.query,
          },
        });
      }

      case 'memrise-membot': {
        return getExternalHref({
          linkType: q.linkType,
          query: {
            languagePairId,
            ...q.query,
          },
        });
      }

      case 'memrise-payment-plans': {
        return getExternalHref(q);
      }
    }
  }, [languagePairId, sourceLanguageLocale, targetLanguageLocale, q]);
}
